import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Film, MoreHorizontal, X as XIcon, Sparkles, ListPlus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MyList() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/my-list`, { withCredentials: true });
      const data = res.data;
      setItems(data);

      const groups = {};
      data.forEach(item => {
        const name = item.list_name || "Watchlist";
        if (!groups[name]) groups[name] = [];
        groups[name].push(item);
      });
      setGrouped(groups);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const removeMovie = async (tmdbId, listName) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/my-list?tmdb_id=${tmdbId}&list_name=${listName}`, { withCredentials: true });
      toast.success("Removed from list");
      fetchList();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const deleteEntireList = async (listName) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/my-list?list_name=${listName}&delete_all=true`, { withCredentials: true });
      toast.success(`Deleted list "${listName}"`);
      fetchList();
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  // Lists are created client-side and persisted to the backend when movies are
  // added via other pages (the POST /api/my-list endpoint creates entries with a
  // list_name). Empty lists are intentionally ephemeral for a responsive UX.
  const handleCreateList = () => {
    const trimmed = newListName.trim();
    if (!trimmed) return;
    if (grouped[trimmed]) {
      toast.error("A list with this name already exists");
      return;
    }
    setGrouped(prev => ({ ...prev, [trimmed]: [] }));
    setNewListName('');
    setCreateDialogOpen(false);
    toast.success(`Created list "${trimmed}"`);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading your collections…</p>
        </div>
      </div>
    );
  }

  const listNames = Object.keys(grouped);
  const hasLists = listNames.length > 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen pt-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="text-4xl font-heading font-bold tracking-tight">My Collections</h1>
            <p className="text-muted-foreground mt-1">
              Curate and organize your personal movie lists.
            </p>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="btn-glow gap-2 self-start sm:self-auto"
          >
            <Plus size={18} />
            Create New List
          </Button>
        </div>

        {/* Empty State — no lists at all */}
        {!hasLists ? (
          <div className="glass-card rounded-2xl border border-white/[0.06] p-12 sm:p-16 flex flex-col items-center text-center max-w-lg mx-auto">
            <div className="relative mb-6">
              <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Film className="h-10 w-10 text-primary" />
              </div>
              <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-secondary" />
              </div>
            </div>
            <h2 className="text-2xl font-heading font-bold mb-2">Start your first collection</h2>
            <p className="text-muted-foreground mb-8 max-w-sm">
              Create custom lists like "Top 10 Sci-Fi" or "Weekend Binge" to organize the movies you love.
            </p>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="btn-glow gap-2"
              size="lg"
            >
              <Plus size={18} />
              Create Your First List
            </Button>
          </div>
        ) : (
          /* List Tabs */
          <Tabs defaultValue={listNames[0]} className="w-full">
            <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-1">
              <TabsList className="glass-card h-auto flex-wrap justify-start gap-2 p-2 border border-white/[0.06]">
                {listNames.map(name => (
                  <TabsTrigger key={name} value={name} className="px-4 py-2 gap-2 data-[state=active]:bg-primary/15 data-[state=active]:text-primary">
                    {name}
                    <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center">
                      {grouped[name]?.length || 0}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCreateDialogOpen(true)}
                    className="shrink-0 border-dashed border-white/20 hover:border-primary/50 hover:bg-primary/5"
                  >
                    <Plus size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Create new list</TooltipContent>
              </Tooltip>
            </div>

            {listNames.map(name => (
              <TabsContent key={name} value={name}>
                {/* List Header with management actions */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-heading font-bold">{name}</h2>
                    <Badge variant="outline" className="text-muted-foreground">
                      {grouped[name]?.length || 0} {grouped[name]?.length === 1 ? 'movie' : 'movies'}
                    </Badge>
                  </div>
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
                            <MoreHorizontal size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>List options</TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive focus:bg-destructive/10 gap-2"
                        onClick={() => setDeleteTarget(name)}
                      >
                        <Trash2 size={14} />
                        Delete List
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Empty list state (list exists but has no movies) */}
                {(!grouped[name] || grouped[name].length === 0) ? (
                  <div className="glass-card rounded-2xl border border-white/[0.06] p-10 flex flex-col items-center text-center">
                    <div className="h-14 w-14 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                      <ListPlus className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-1">This list is empty</h3>
                    <p className="text-muted-foreground text-sm max-w-xs">
                      Browse movies and add them to "{name}" to start building this collection.
                    </p>
                  </div>
                ) : (
                  /* Movie Grid */
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {grouped[name].map(movie => (
                      <div key={movie.tmdb_id} className="relative group">
                        {/* Gradient border wrapper */}
                        <div className="rounded-xl p-[1px] bg-gradient-to-b from-white/10 to-transparent transition-all duration-300 group-hover:from-primary/40 group-hover:to-secondary/20">
                          <div className="rounded-xl overflow-hidden bg-card">
                            <Link to={`/movie/${movie.tmdb_id}`} className="block relative overflow-hidden">
                              <img
                                src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                                alt={movie.title}
                                className="w-full aspect-[2/3] object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                              />
                              {/* Hover overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </Link>
                            {/* Remove button */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => removeMovie(movie.tmdb_id, name)}
                                  className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/80 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-destructive hover:text-white hover:scale-110"
                                >
                                  <XIcon size={14} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Remove from list</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                        <p className="mt-2 text-sm font-medium truncate px-0.5">{movie.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Create List Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="glass-card border-white/[0.08] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New List</DialogTitle>
              <DialogDescription>
                Give your collection a name. You can add movies to it later.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Input
                placeholder={'e.g. "Top 10 Sci-Fi", "Weekend Binge"'}
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
                className="bg-white/5 border-white/10 focus-visible:ring-primary/50"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setCreateDialogOpen(false); setNewListName(''); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateList} disabled={!newListName.trim()} className="btn-glow">
                Create List
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent className="glass-card border-white/[0.08]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Delete "{deleteTarget}"?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove the list and all its movies. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  deleteEntireList(deleteTarget);
                  setDeleteTarget(null);
                }}
              >
                Delete List
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
